"use client";

import { usePathname } from "next/navigation";
import { useEffect, useRef } from "react";
import { useToast } from "@/components/feedback";
import type { CalculationCycle } from "@/features/tuition/types";
import { getCycleFailureMessage } from "@/features/tuition/utils/cycle-display";
import { PageTypeAdmin } from "@/lib/types/page-types";
import { CalculationCycleStatus } from "@/openapi/generated";

export const TUITION_CALCULATION_COMPLETION_TOAST_ID =
  "tuition-calculation-completion";

interface UseTuitionCalculationCompletionToastProps {
  cycle: CalculationCycle | null | undefined;
  tracking: boolean;
}

export function useTuitionCalculationCompletionToast({
  cycle,
  tracking,
}: UseTuitionCalculationCompletionToastProps) {
  const toast = useToast();
  const pathname = usePathname();
  const lastStatusRef = useRef<CalculationCycleStatus | null>(null);
  const isOnTuitionPage = pathname === PageTypeAdmin.Tuition;

  useEffect(() => {
    if (!tracking) {
      lastStatusRef.current = null;
      return;
    }

    const status = cycle?.status;
    if (
      status !== CalculationCycleStatus.Calculated &&
      status !== CalculationCycleStatus.CalculationFailed
    ) {
      return;
    }

    if (lastStatusRef.current === status) return;
    lastStatusRef.current = status;

    if (!cycle) return;

    const processed = Number(cycle.processedCount ?? 0);

    if (status === CalculationCycleStatus.Calculated) {
      toast({
        id: TUITION_CALCULATION_COMPLETION_TOAST_ID,
        title: "Tuition calculated",
        description: isOnTuitionPage
          ? `Calculation finished for ${processed} student${processed === 1 ? "" : "s"}. Review amounts and post when you are ready.`
          : `Calculation finished for ${processed} student${processed === 1 ? "" : "s"}. Open Tuition to review amounts and post.`,
        status: "success",
        duration: 10_000,
        isClosable: true,
        position: "top-right",
      });
      return;
    }

    toast({
      id: TUITION_CALCULATION_COMPLETION_TOAST_ID,
      title: "Calculation failed",
      description:
        getCycleFailureMessage(status) ??
        (isOnTuitionPage
          ? "Tuition calculation failed. Close cycle or Cancel cycle to continue."
          : "Tuition calculation failed. Open Tuition to close or cancel the cycle."),
      status: "error",
      duration: null,
      isClosable: true,
      position: "top-right",
    });
  }, [cycle?.processedCount, cycle?.status, isOnTuitionPage, toast, tracking]);
}
